import React, { useState, useCallback, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { FaTrash, FaCheck, FaShip, FaCopy, FaArrowLeft } from 'react-icons/fa';
import { GrClearOption } from "react-icons/gr";
import { BiRotateRight } from 'react-icons/bi';
import { getDatabase, ref, get, update, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { getGridSize, getShipConfiguration } from '../services/gameService';

// Mobile uses touch backend
const DndBackend = TouchBackend;

// Default ship configuration - will be overridden by room settings
const DEFAULT_SHIPS = [
  { id: 'carrier', name: 'Carrier', size: 5, color: 'bg-blue-500' },
  { id: 'battleship', name: 'Battleship', size: 4, color: 'bg-green-500' },
  { id: 'cruiser', name: 'Cruiser', size: 3, color: 'bg-yellow-500' },
  { id: 'destroyer', name: 'Destroyer', size: 2, color: 'bg-red-500' },
  { id: 'scout', name: 'Scout', size: 2, color: 'bg-violet-500' }
];

// Mobile Ship component - optimized for touch
const Ship = ({ ship, isPlaced, onClick, rotation = 0, position, disabled }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'ship',
    item: () => ({
      id: ship.id,
      ship: { ...ship },
      rotation,
      isPlaced,
      position,
    }),
    canDrag: !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [ship.id, rotation, isPlaced, disabled]);

  const handleTouchStart = () => {
    // Don't prevent default so dragging works
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) onClick();
  };

  return (
    <div
      ref={drag}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      className={`
        ship-draggable flex flex-row relative
        ${isPlaced ? 'cursor-move' : disabled ? 'cursor-not-allowed opacity-50' : 'cursor-move hover:scale-105'} 
        transition-all duration-300
        ${isDragging ? 'opacity-50' : 'opacity-100'}
        select-none
      `}
      style={{
        transform: `rotate(${rotation}deg)`,
        transformOrigin: 'center',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
        gap: '0px',
      }}
    >      {Array(ship.size).fill(null).map((_, i) => (
      <div
        key={i}
        className={`
            w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 ${ship.color}
            border border-gray-600 rounded
            transition-all duration-200
            select-none
          `}
      />
    ))}
      {!isPlaced && (
        <div className="absolute -top-6 left-0 text-white text-xs 
                      bg-gray-800/90 px-1 py-0.5 rounded-md flex items-center gap-1
                      whitespace-nowrap select-none">
          <FaShip size={10} />
          {ship.name}
        </div>
      )}
    </div>
  );
};


// Dock Ship Component - Card style for the bottom dock
const DockShip = ({ ship, isPlaced, isSelected, onClick }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'ship',
    item: () => ({
      id: ship.id,
      ship: { ...ship },
      rotation: 0,
      isPlaced: false,
      position: null,
    }),
    canDrag: !isPlaced,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [ship, isPlaced]);

  return (
    <div
      ref={drag}
      onClick={onClick}
      className={`
        relative flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-300
        ${isPlaced
          ? 'opacity-30 grayscale cursor-default scale-90'
          : 'cursor-grab active:cursor-grabbing hover:scale-105 active:scale-95 shadow-lg bg-slate-700/50 border border-slate-600/50 backdrop-blur-sm'
        }
        ${isSelected && !isPlaced ? 'ring-2 ring-blue-500 shadow-blue-500/20 shadow-xl scale-105 bg-slate-700' : ''}
        ${isDragging ? 'opacity-50' : 'opacity-100'}
      `}
      style={{ minWidth: '70px', height: '70px' }}
    >
      {/* Mini Ship Representation */}
      <div className="flex gap-0.5 pointer-events-none mb-1">
        {Array(ship.size).fill(null).map((_, i) => (
          <div key={i} className={`w-2 h-2 rounded-[1px] ${ship.color} shadow-sm`} />
        ))}
      </div>
      <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest max-w-[65px] truncate">
        {ship.name}
      </span>
    </div>
  );
};

// PlacedShipCell Component - Draggable cell for placed ships
const PlacedShipCell = ({ ship, isSelected, isShipStart, shipRotation, disabled, onSelect }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'ship',
    item: () => ({
      id: ship.id,
      ship: { ...ship },
      rotation: shipRotation,
      isPlaced: true,
      position: null,
    }),
    canDrag: !disabled && isShipStart, // Only first cell is draggable
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [ship, isShipStart, shipRotation, disabled]);

  return (
    <div
      ref={isShipStart ? drag : null}
      className={`w-full h-full ${ship.color} rounded border-2 ${isSelected ? 'border-white' : 'border-gray-600'
        } ${isShipStart && !disabled ? 'cursor-move' : 'cursor-pointer'} transition-all ${isDragging ? 'opacity-50' : 'opacity-100'
        }`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    />
  );
};

// Mobile Cell component - larger touch targets
const Cell = ({ x, y, onDrop, onHover, onClick, isValidPlacement, children, disabled, gridSize = 8 }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'ship',
    drop: (item) => {
      if (!disabled) {
        onDrop(x, y, item.ship, item.rotation === 90 || item.rotation === 270);
      }
      return undefined;
    },
    hover: (item) => {
      if (!disabled) {
        onHover(x, y, item.ship, item.rotation === 90 || item.rotation === 270);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  }), [x, y, onDrop, onHover, disabled]);

  const handleTouchStart = (e) => {
    if (e.target.closest('.ship-draggable')) {
      return;
    }
    e.preventDefault();
  };

  const handleClick = () => {
    if (!disabled && onClick) onClick();
  };
  const colLabel = String.fromCharCode(65 + x); // A-H
  const rowLabel = gridSize - y; // 8 down to 1
  const label = `${colLabel}${rowLabel}`;

  return (
    <div
      ref={drop}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      className={`
        w-full h-full border border-gray-600 rounded
        transition-all duration-200 select-none touch-none relative
        flex items-center justify-center group
        ${isOver && isValidPlacement ? 'bg-green-500/50 border-green-400 animate-pulse' : ''}
        ${isOver && !isValidPlacement ? 'bg-red-500/50 border-red-400 animate-pulse' : ''}
        ${!isOver ? 'bg-gray-700 hover:bg-gray-600' : ''}
      `}
      title={`Cell ${label}`}
    >
      {children}
      {!children && (
        <span className="text-[12px] text-gray-500/50 group-hover:text-gray-400 font-mono">
          {label}
        </span>
      )}
    </div>
  );
};

const ShipPlacement_Mobile = () => {
  const navigate = useNavigate();

  // State for dynamic configuration
  const [GRID_SIZE, setGridSize] = useState(8);
  const [SHIPS, setShips] = useState(DEFAULT_SHIPS);
  const [MAX_SHIPS, setMaxShips] = useState(5);
  const [, setRoomData] = useState(null);
  const [roomId, setRoomId] = useState('');
  const [_isHost, setIsHost] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Existing state
  const [grid, setGrid] = useState([]);
  const [placedShips, setPlacedShips] = useState(new Map());
  const [selectedShip, setSelectedShip] = useState(null);
  const [shipRotations, setShipRotations] = useState(new Map());
  const [hoverCoords, setHoverCoords] = useState(null);
  const [catalogQueue, setCatalogQueue] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [, setStatusMessage] = useState('');

  // Initialize grid based on size
  const createEmptyGrid = useCallback((size) => {
    return Array(size).fill().map(() => Array(size).fill(null));
  }, []);

  // Load room settings and configure game
  useEffect(() => {
    const loadRoomSettings = async () => {
      try {
        const currentRoomId = localStorage.getItem('battleshipRoomId');
        const currentPlayerId = localStorage.getItem('battleshipPlayerId');

        if (!currentRoomId) {
          setGrid(createEmptyGrid(8));
          setCatalogQueue([...DEFAULT_SHIPS]);
          return;
        }

        setRoomId(currentRoomId);

        const db = getDatabase();
        const roomRef = ref(db, `rooms/${currentRoomId}`);
        const snapshot = await get(roomRef);
        const roomData = snapshot.val();

        if (roomData) {
          setRoomData(roomData);

          if (currentPlayerId && roomData.hostId) {
            setIsHost(currentPlayerId === roomData.hostId);
          }

          console.log('Room data loaded:', {
            roomId: currentRoomId,
            gameMode: roomData.gameMode,
            hostId: roomData.hostId,
            currentPlayerId,
            isHost: currentPlayerId === roomData.hostId
          });

          if (roomData.settings) {
            const gridSize = getGridSize(roomData.settings);
            const ships = getShipConfiguration(roomData.settings);

            setGridSize(gridSize);
            setShips(ships);
            setMaxShips(ships.length);
            setGrid(createEmptyGrid(gridSize));
            setCatalogQueue([...ships]);
          } else {
            setGrid(createEmptyGrid(8));
            setCatalogQueue([...DEFAULT_SHIPS]);
          }
        } else {
          setGrid(createEmptyGrid(8));
          setCatalogQueue([...DEFAULT_SHIPS]);
        }
      } catch (error) {
        console.error('Error loading room settings:', error);
        setGrid(createEmptyGrid(8));
        setCatalogQueue([...DEFAULT_SHIPS]);
      }
    };

    loadRoomSettings();
  }, [createEmptyGrid]);

  const checkValidPlacement = useCallback((x, y, ship, vertical) => {
    if (!ship) return false;
    if (!grid || grid.length === 0) return false;

    const size = ship.size;

    // Check bounds
    if (vertical && y + size > GRID_SIZE) return false;
    if (!vertical && x + size > GRID_SIZE) return false;
    if (x < 0 || y < 0) return false;

    // Check if cells are occupied by other ships
    for (let i = 0; i < size; i++) {
      const checkX = vertical ? x : x + i;
      const checkY = vertical ? y + i : y;

      if (checkY >= GRID_SIZE || checkX >= GRID_SIZE) return false;

      const cellContent = grid[checkY] && grid[checkY][checkX];
      if (cellContent && cellContent !== ship.id) return false;
    }

    if (!placedShips.has(ship.id) && placedShips.size >= MAX_SHIPS) return false;

    return true;
  }, [grid, placedShips, GRID_SIZE, MAX_SHIPS]);

  const handleDrop = (x, y, ship, shipIsVertical) => {
    if (!checkValidPlacement(x, y, ship, shipIsVertical)) {
      return;
    }

    // Clear previous placement of this ship
    const newGrid = grid.map(row => row.map(cell => cell === ship.id ? null : cell));

    // Place ship in new position
    for (let i = 0; i < ship.size; i++) {
      const placeX = shipIsVertical ? x : x + i;
      const placeY = shipIsVertical ? y + i : y;
      if (placeY >= 0 && placeY < GRID_SIZE && placeX >= 0 && placeX < GRID_SIZE) {
        newGrid[placeY][placeX] = ship.id;
      }
    }

    setGrid(newGrid);

    const newPlacedShips = new Map(placedShips);
    newPlacedShips.set(ship.id, { x, y, vertical: shipIsVertical });
    setPlacedShips(newPlacedShips);

    const newRotations = new Map(shipRotations);
    newRotations.set(ship.id, shipIsVertical ? 90 : 0);
    setShipRotations(newRotations);

    if (!placedShips.has(ship.id)) {
      setCatalogQueue(current => current.filter(s => s.id !== ship.id));
    }

    setSelectedShip(ship);
  };

  const handleRotateShip = () => {
    if (!selectedShip) return;

    const shipPosition = placedShips.get(selectedShip.id);
    if (!shipPosition) return;

    const newVertical = !shipPosition.vertical;
    if (checkValidPlacement(shipPosition.x, shipPosition.y, selectedShip, newVertical)) {
      handleDrop(shipPosition.x, shipPosition.y, selectedShip, newVertical);
    }
  };

  const handleHover = (x, y, ship, shipIsVertical) => {
    setHoverCoords({ x, y, ship, isVertical: shipIsVertical });
  };

  const handleClearPlacement = () => {
    setGrid(Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(null)));
    setPlacedShips(new Map());
    setShipRotations(new Map());
    setSelectedShip(null);
    setCatalogQueue([...SHIPS].slice(0, MAX_SHIPS));
  };

  const handleAutoPlace = () => {
    const newGrid = Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(null));
    const newPlacedShips = new Map();
    const newRotations = new Map();

    SHIPS.forEach((ship) => {
      let placed = false;
      const maxAttempts = 100;
      let attempts = 0;

      while (!placed && attempts < maxAttempts) {
        const x = Math.floor(Math.random() * GRID_SIZE);
        const y = Math.floor(Math.random() * GRID_SIZE);
        const vertical = Math.random() < 0.5;

        const fitsHorizontally = !vertical && x + ship.size <= GRID_SIZE;
        const fitsVertically = vertical && y + ship.size <= GRID_SIZE;

        if ((fitsHorizontally || fitsVertically) && x >= 0 && y >= 0) {
          let canPlace = true;
          for (let i = 0; i < ship.size; i++) {
            const checkX = vertical ? x : x + i;
            const checkY = vertical ? y + i : y;
            if (newGrid[checkY][checkX]) {
              canPlace = false;
              break;
            }
          }

          if (canPlace) {
            for (let i = 0; i < ship.size; i++) {
              const placeX = vertical ? x : x + i;
              const placeY = vertical ? y + i : y;
              newGrid[placeY][placeX] = ship.id;
            }

            newPlacedShips.set(ship.id, { x, y, vertical });
            newRotations.set(ship.id, vertical ? 90 : 0);
            placed = true;
          }
        }
        attempts++;
      }
    });

    setGrid(newGrid);
    setPlacedShips(newPlacedShips);
    setShipRotations(newRotations);
    setCatalogQueue([]);
    setSelectedShip(null);
  };

  const updateShipPlacement = async (roomId, playerId, placementData) => {
    try {
      const db = getDatabase();
      const playerRef = ref(db, `rooms/${roomId}/players/${playerId}`);
      const playerSnapshot = await get(playerRef);

      if (!playerSnapshot.exists()) {
        console.error("Player not found in room");
        return false;
      }

      const updates = {};
      updates[`rooms/${roomId}/players/${playerId}/PlacementData`] = placementData;

      await update(ref(db), updates);
      console.log("Ship placement data updated successfully");
      return true;
    } catch (error) {
      console.error("Error updating ship placement:", error);
      return false;
    }
  };

  const handleComplete = async () => {
    if (placedShips.size === SHIPS.length) {
      const playerId = localStorage.getItem('battleshipPlayerId');
      const roomId = localStorage.getItem('battleshipRoomId');

      if (!playerId || !roomId) {
        setErrorMessage("You must be in a room to place ships");
        return;
      }

      setIsSaving(true);
      setErrorMessage('');

      try {
        const shipPlacements = {};
        placedShips.forEach((position, shipId) => {
          const ship = SHIPS.find(s => s.id === shipId);
          shipPlacements[shipId] = {
            name: ship.name,
            startPosition: { x: position.x, y: position.y },
            orientation: position.vertical ? 'vertical' : 'horizontal',
            size: ship.size
          };
        });

        const gridForDatabase = Array(GRID_SIZE).fill().map(() =>
          Array(GRID_SIZE).fill().map(() => ({
            ship: null,
            hit: false,
            miss: false
          }))
        );

        placedShips.forEach((position, shipId) => {
          const ship = SHIPS.find(s => s.id === shipId);
          for (let i = 0; i < ship.size; i++) {
            const x = position.vertical ? position.x : position.x + i;
            const y = position.vertical ? position.y + i : position.y;
            gridForDatabase[y][x].ship = shipId;
          }
        });

        const placementData = {
          grid: gridForDatabase,
          ships: shipPlacements,
          lastUpdated: Date.now()
        };

        const success = await updateShipPlacement(roomId, playerId, placementData);

        if (!success) {
          throw new Error('Failed to update ship placement');
        }

        setIsSaving(false);
        setIsSaved(true);

      } catch (error) {
        setErrorMessage(error.message);
        setIsSaving(false);
      }
    }
  };

  const handleReady = async () => {
    console.log("handleReady called, isSaved:", isSaved);

    if (isSaved) {
      try {
        const roomId = localStorage.getItem('battleshipRoomId');
        const playerId = localStorage.getItem('battleshipPlayerId');
        const db = getDatabase();

        await update(ref(db, `rooms/${roomId}/players/${playerId}`), {
          ready: true
        });

        const roomRef = ref(db, `rooms/${roomId}`);
        const snapshot = await get(roomRef);
        const room = snapshot.val();

        if (!room) {
          setErrorMessage("Room not found");
          return;
        }

        const gameMode = room.gameMode || 'admin';
        const players = Object.values(room.players || {});
        const allPlayersReady = players.length === 2 && players.every(player => player.ready);

        console.log('AI Game Debug:', {
          gameMode,
          playerCount: players.length,
          players: players.map(p => ({ name: p.name, ready: p.ready, isAI: p.isAI })),
          allPlayersReady
        });

        if (allPlayersReady) {
          if (gameMode === 'random' || gameMode === 'friendly' || gameMode === 'ai') {
            setStatusMessage("Both players ready! Starting game in 3 seconds...");

            setTimeout(() => {
              setStatusMessage("Game starting in 2...");
              setTimeout(() => {
                setStatusMessage("Game starting in 1...");
                setTimeout(async () => {
                  try {
                    if (gameMode === 'ai') {
                      const { startAiGame } = await import('../services/aiGameService');
                      await startAiGame(roomId);
                    } else {
                      const { startGame } = await import('../services/adminService');
                      await startGame(roomId);
                    }
                    setStatusMessage("Starting game...");
                  } catch (error) {
                    console.error("Error starting game:", error);
                    setStatusMessage("Error starting game. Please try again...");
                  }
                }, 1000);
              }, 1000);
            }, 1000);
          } else {
            setStatusMessage("Both players ready! Waiting for admin to start the game...");
          }
        } else {
          setStatusMessage("Waiting for other player to get ready...");
        }
      } catch (error) {
        console.error("Error in handleReady:", error);
        setErrorMessage(error.message);
      }
    } else {
      setErrorMessage("Please save your ship placement first");
    }
  };

  const handleDeleteShip = (shipId) => {
    const shipToAdd = SHIPS.find(s => s.id === shipId);

    const newGrid = grid.map(row =>
      row.map(cell => cell === shipId ? null : cell)
    );
    setGrid(newGrid);

    const newPlacedShips = new Map(placedShips);
    newPlacedShips.delete(shipId);
    setPlacedShips(newPlacedShips);

    if (shipToAdd && !catalogQueue.some(s => s.id === shipId)) {
      setCatalogQueue(current => [...current, shipToAdd]);
    }

    const newRotations = new Map(shipRotations);
    newRotations.delete(shipId);
    setShipRotations(newRotations);

    setSelectedShip(null);
  };

  const handleGridCellClick = (x, y) => {
    if (isSaved) return;
    const shipId = grid[y][x];

    if (shipId) {
      const ship = SHIPS.find(s => s.id === shipId);
      setSelectedShip(ship);
    } else if (selectedShip && !placedShips.has(selectedShip.id)) {
      const isVertical = (shipRotations.get(selectedShip.id) || 0) === 90;
      if (checkValidPlacement(x, y, selectedShip, isVertical)) {
        handleDrop(x, y, selectedShip, isVertical);
      }
    }
  };

  useEffect(() => {
    const roomId = localStorage.getItem('battleshipRoomId');
    const playerId = localStorage.getItem('battleshipPlayerId');

    if (!roomId || !playerId) return;

    const roomRef = ref(getDatabase(), `rooms/${roomId}`);

    const unsubscribe = onValue(roomRef, (snapshot) => {
      const room = snapshot.val();
      if (!room || !room.players) return;

      const players = Object.entries(room.players);
      if (players.length < 2) return;

      if (isSaved) {
        const otherPlayer = players.find(([id]) => id !== playerId);
        const gameMode = room.gameMode || 'admin';

        if (otherPlayer && !otherPlayer[1].ready) {
          setStatusMessage("Waiting for other player to get ready...");
        } else {
          // Check if it's an AI game or auto-start game mode
          if (gameMode === 'ai' || gameMode === 'random' || gameMode === 'friendly') {
            setStatusMessage("Both players ready! Starting game automatically...");
          } else {
            setStatusMessage("Both players ready! Waiting for admin to start the game...");
          }
        }
      }

      if (room.countdown) {
        setStatusMessage(`Game starting in ${room.countdown}...`);
      }

      if (room.gameStarted) {
        navigate(`/room/${roomId}`);
      }
    });

    return () => unsubscribe();
  }, [isSaved, navigate]);

  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.body.style.touchAction = 'none';

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.body.style.touchAction = '';
    };
  }, []);

  const handleCopyRoomId = async () => {
    console.log('Copy button clicked, roomId:', roomId);

    if (!roomId) {
      console.error('No room ID to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(roomId);
      console.log('Successfully copied to clipboard:', roomId);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.log('Clipboard API failed, using fallback:', error);
      try {
        const textArea = document.createElement('textarea');
        textArea.value = roomId;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          console.log('Fallback copy successful');
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        } else {
          console.error('Fallback copy failed');
        }
      } catch (fallbackError) {
        console.error('Both clipboard methods failed:', fallbackError);
      }
    }
  };

  return (
    <DndProvider backend={DndBackend}>
      <div className="h-screen w-full bg-slate-900 flex flex-col overflow-hidden select-none p-2">

        {/* --- HEADER (7%) --- */}
        <div className="h-[7%] min-h-[50px] bg-slate-900 flex items-center justify-between px-2 mb-2 rounded-xl border border-slate-800/50 shadow-sm relative z-10">

          {/* Left: Back/Exit */}
          <button
            onClick={() => navigate('/')}
            className="p-1.5 text-slate-400 hover:text-white transition-colors rounded-full active:bg-slate-800"
          >
            <FaArrowLeft size={16} />
          </button>

          {/* Center: Room Code Pill */}
          {roomId && (
            <button
              onClick={handleCopyRoomId}
              className="bg-slate-800 px-3 py-1 rounded-full border border-slate-700 flex items-center gap-2 active:scale-95 transition-transform"
            >
              <span className="text-slate-200 font-mono text-xs font-medium tracking-wider">{roomId}</span>
              {copySuccess ? <FaCheck size={10} className="text-green-400" /> : <FaCopy size={10} className="text-slate-500" />}
            </button>
          )}

          {/* Right: Placement Counter */}
          <div className="text-xs font-medium text-slate-400">
            <span className={placedShips.size === SHIPS.length ? "text-green-400" : "text-slate-200"}>
              {placedShips.size}
            </span>
            <span className="text-slate-600">/</span>
            <span className="text-slate-500">{SHIPS.length}</span>
          </div>
        </div>

        {/* --- STAGE: BATTLE GRID (63%) --- */}
        <div className="h-[63%] w-full relative flex items-center justify-center bg-slate-900 overflow-hidden mb-2">
          {/* Container with rounded corners and inner shadow */}
          <div className="w-[98%] h-[98%] bg-slate-800/50 rounded-2xl shadow-[inset_0_2px_10px_rgba(0,0,0,0.3)] flex items-center justify-center relative border border-slate-800/50">

            <div className="text-center">
              {/* Grid itself - Updated with uniform spacing */}
              <div
                className="grid gap-1 bg-slate-900/50 p-4 rounded-xl border border-slate-700/30 shadow-2xl relative z-10"
                style={{
                  gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
                  gridTemplateRows: `repeat(${GRID_SIZE}, 1fr)`,
                  width: 'min(90vw, 90vh)',
                  height: 'min(90vw, 90vh)'
                }}
              >
                {grid.map((row, y) =>
                  row.map((cell, x) => {
                    const ship = cell ? SHIPS.find(s => s.id === cell) : null;
                    const isSelected = selectedShip?.id === cell;
                    const shipPosition = ship ? placedShips.get(ship.id) : null;
                    const shipRotation = ship ? (shipRotations.get(ship.id) || 0) : 0;

                    // Only make the first cell of the ship draggable to avoid conflicts
                    const isShipStart = ship && shipPosition &&
                      shipPosition.x === x && shipPosition.y === y;

                    return (
                      <Cell
                        key={`${x}-${y}`}
                        x={x}
                        y={y}
                        onDrop={handleDrop}
                        onHover={handleHover}
                        onClick={() => handleGridCellClick(x, y)}
                        disabled={isSaved}
                        gridSize={GRID_SIZE}
                        isValidPlacement={
                          hoverCoords &&
                          !isSaved &&
                          checkValidPlacement(
                            hoverCoords.x,
                            hoverCoords.y,
                            hoverCoords.ship,
                            hoverCoords.isVertical
                          )
                        }
                      >
                        {ship && (
                          <PlacedShipCell
                            ship={ship}
                            isSelected={isSelected}
                            isShipStart={isShipStart}
                            shipRotation={shipRotation}
                            disabled={isSaved}
                            onSelect={() => setSelectedShip(ship)}
                          />
                        )}
                      </Cell>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* --- DOCK: CONTROL CENTER (30%) --- */}
        <div className="h-[30%] bg-slate-800 flex flex-col rounded-2xl shadow-lg relative z-20 overflow-hidden border border-slate-700">

          {/* Row 1: Fleet Carousel (45% of dock) */}
          <div className="h-[45%] overflow-x-auto overflow-y-hidden flex items-center px-4 scroll-smooth no-scrollbar border-b border-slate-700/50">
            <div className="flex gap-4 min-w-max mx-auto items-center h-full">
              {SHIPS.map((ship) => {
                const isPlaced = placedShips.has(ship.id);
                const isSelected = selectedShip?.id === ship.id;

                return (
                  <DockShip
                    key={ship.id}
                    ship={ship}
                    isPlaced={isPlaced}
                    isSelected={isSelected}
                    onClick={() => {
                      if (!isPlaced) setSelectedShip(ship);
                      else setSelectedShip(ship);
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Row 2: Toolbelt (30% of dock) */}
          <div className="h-[30%] bg-slate-900/40 px-4 flex items-center justify-center backdrop-blur-md">
            <div className="grid grid-cols-4 gap-3 w-full max-w-sm h-[70%]">

              {/* Rotate */}
              <button
                onClick={handleRotateShip}
                disabled={!selectedShip || !placedShips.has(selectedShip?.id) || isSaved}
                className="h-full flex items-center justify-center bg-slate-800/80 rounded-xl shadow-sm border border-slate-700/50 active:scale-95 text-slate-300 disabled:opacity-30 disabled:active:scale-100 hover:bg-slate-700 hover:border-slate-600 transition-all"
              >
                <BiRotateRight size={22} />
              </button>

              {/* Auto-Place */}
              <button
                onClick={handleAutoPlace}
                disabled={catalogQueue.length === 0 || isSaved}
                className="h-full flex items-center justify-center bg-blue-600/90 text-white rounded-xl shadow-lg shadow-blue-900/30 active:scale-95 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-600 disabled:shadow-none disabled:active:scale-100 border border-blue-500/50 transition-all"
              >
                <FaShip size={18} />
              </button>

              {/* Clear */}
              <button
                onClick={handleClearPlacement}
                disabled={placedShips.size === 0 || isSaved}
                className="h-full flex items-center justify-center bg-slate-800/80 rounded-xl shadow-sm border border-slate-700/50 active:scale-95 text-slate-300 disabled:opacity-30 disabled:active:scale-100 hover:bg-slate-700 hover:border-slate-600 transition-all"
              >
                <GrClearOption size={16} />
              </button>

              {/* Delete (Selected) */}
              <button
                onClick={() => selectedShip && placedShips.has(selectedShip.id) && handleDeleteShip(selectedShip.id)}
                disabled={!selectedShip || !placedShips.has(selectedShip?.id) || isSaved}
                className="h-full flex items-center justify-center bg-red-500/10 text-red-400 rounded-xl shadow-sm border border-red-500/20 active:scale-95 hover:bg-red-500/20 disabled:opacity-30 disabled:bg-slate-800 disabled:text-slate-600 disabled:border-slate-700 disabled:active:scale-100 transition-all"
              >
                <FaTrash size={16} />
              </button>

            </div>
          </div>

          {/* Row 3: Action Button (25% of dock) */}
          <div className="h-[25%] flex items-center justify-center bg-slate-800 p-3">
            {isSaved ? (
              <button
                onClick={handleReady}
                className="w-full h-full max-h-12 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-2xl font-bold text-sm tracking-widest shadow-lg shadow-blue-900/30 active:scale-[0.98] transition-all flex items-center justify-center gap-2 border border-blue-500/50 animate-pulse"
              >
                READY TO BATTLE!
              </button>
            ) : placedShips.size === SHIPS.length ? (
              <button
                onClick={handleComplete}
                disabled={isSaving}
                className="w-full h-full max-h-12 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white rounded-2xl font-bold text-sm tracking-widest shadow-lg shadow-emerald-900/30 active:scale-[0.98] transition-all flex items-center justify-center gap-2 border border-emerald-500/50"
              >
                {isSaving ? 'SAVING...' : 'CONFIRM LAYOUT'}
              </button>
            ) : (
              <button
                disabled
                className="w-full h-full max-h-12 bg-slate-800 text-slate-600 rounded-2xl font-bold text-xs tracking-widest border border-slate-700/50 shadow-inner"
              >
                PLACE ALL SHIPS
              </button>
            )}
          </div>

        </div>

        {/* Global Toast for Errors */}
        {errorMessage && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-full shadow-xl z-50 text-sm font-semibold animate-fade-in-down">
            {errorMessage}
          </div>
        )}

      </div>
    </DndProvider>
  );
};

export default ShipPlacement_Mobile;
